/** Conversational visa status steps for the terminal assistant. */
import { todayISO, fmtAmt } from './utils.js';
import {
  parseSmartDateOnly,
  parseSmartDateTime,
  validateSmartDate,
  validateSmartDateTime,
  DATE_HINT,
  DATETIME_HINT,
  datetimeToDate,
  isSkip,
} from './assistant-datetime.js';
import { upperDoc, capitalizeName } from './assistant-ticket-new.js';

function sd(lead, status) {
  return lead?.extra?.stage_data?.[status] || {};
}

function pick(...vals) {
  return vals.find((v) => v != null && v !== '');
}

function parseLkrAmount(v) {
  const n = Number(String(v ?? '').trim().replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

export function visaStageSteps(status, lead = {}) {
  const extra = lead.extra || {};
  const stage = sd(lead, status);

  switch (status) {
    case 'new':
      return [];
    case 'details_provided':
      return [
        {
          key: 'quote_price',
          ask: (lead) => {
            const extra = lead?.extra || {};
            const pax = extra.pax || {};
            const billable = (pax.adt || 0) + (pax.chd || 0) || pax.adt || 1;
            const fromRates = extra.pax_rates?.adt != null
              ? Math.round(extra.pax_rates.adt * billable)
              : null;
            const hint = fromRates != null ? ` (currently ${fmtAmt(fromRates)} for ${billable} pax)` : '';
            return `Total quoted price (LKR) for all passengers?${hint}`;
          },
          isSaved: (lead) => {
            const stage = sd(lead, 'details_provided');
            return stage.quote_price != null && stage.quote_price !== '';
          },
          validate: (v) => {
            const n = parseLkrAmount(v);
            if (!Number.isFinite(n) || n <= 0) return 'Enter quoted amount in LKR.';
            return null;
          },
          parse: (v) => Math.round(parseLkrAmount(v)),
          default: (_answers, lead) => {
            const stage = sd(lead, 'details_provided');
            const v = pick(stage.quote_price, lead.amount, '');
            return v !== '' && v != null ? v : '';
          },
        },
        {
          key: 'note',
          ask: 'Notes for client?',
          hint: 'enter to skip',
          optional: true,
          parse: (v) => (isSkip(v) ? null : String(v).trim()),
        },
      ];
    case 'visa_applied':
      return [
        {
          key: 'application_id',
          ask: 'Application / reference ID?',
          isSaved: (lead) => {
            const s = sd(lead, 'visa_applied');
            return s.application_id != null && s.application_id !== '';
          },
          validate: (v) => (!String(v).trim() ? 'Application ID is required.' : null),
          parse: (v) => String(v).trim(),
          default: (_answers, lead) => {
            const s = sd(lead, 'visa_applied');
            return pick(s.application_id, lead.extra?.application_id, '');
          },
        },
        {
          key: 'passport_no',
          ask: 'Passport number?',
          isSaved: (lead) => {
            const s = sd(lead, 'visa_applied');
            return s.passport_no != null && s.passport_no !== '';
          },
          validate: (v) => (!String(v).trim() ? 'Passport number is required.' : null),
          parse: (v) => upperDoc(v),
          default: (_answers, lead) => {
            const s = sd(lead, 'visa_applied');
            return pick(s.passport_no, lead.extra?.passport_no, '');
          },
        },
      ];
    case 'visa_issued':
      return [
        {
          key: 'issue_date',
          ask: 'Visa issue date?',
          hint: DATE_HINT,
          validate: validateSmartDate,
          parse: (v) => parseSmartDateOnly(v) || todayISO(),
          default: () => pick(extra.visa_issued_date, stage.issue_date, todayISO()),
        },
        {
          key: 'note',
          ask: 'Notes?',
          optional: true,
          parse: (v) => (isSkip(v) ? null : String(v).trim()),
        },
      ];
    default:
      return [];
  }
}

export function buildVisaStatusPatchBody(status, lead, answers = {}) {
  const d = { ...answers };
  const body = { status, stage_data: {} };

  if (status === 'details_provided') {
    body.amount = d.quote_price;
    body.stage_data = { quote_price: d.quote_price, note: d.note || null };
    if (d.note) body.note = d.note;
    return body;
  }
  if (status === 'visa_applied') {
    const extra = lead.extra || {};
    body.stage_data = {
      application_id: d.application_id,
      passport_no: d.passport_no || extra.passport_no || null,
    };
    return body;
  }
  if (status === 'visa_issued') {
    body.stage_data = { issue_date: d.issue_date, note: d.note || null };
    if (d.note) body.note = d.note;
    return body;
  }
  return body;
}

export function validateVisaStageAnswers(status, lead, answers = {}) {
  const body = buildVisaStatusPatchBody(status, lead, answers);
  if (status === 'details_provided' && !body.amount) return 'Enter quoted price.';
  if (status === 'visa_applied') {
    if (!body.stage_data.application_id) return 'Enter application ID.';
    if (!body.stage_data.passport_no) return 'Enter passport number.';
  }
  if (status === 'visa_issued' && !body.stage_data.issue_date) return 'Enter issue date.';
  return null;
}

export { DATE_HINT, DATETIME_HINT, parseSmartDateOnly, parseSmartDateTime, datetimeToDate };
